import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  Button,
  type ButtonSize,
  type PlainDate,
  type TimePickerValue,
} from '@viquard/doc-ui/components';
import type {
  CalendarEvent,
  CalendarEventLink,
  CalendarGroup,
  CalendarKindMap,
} from '../../types.js';
import { useCalendarMessages } from '../../i18n.js';
import {
  resolveKindDefaults,
  type CalendarKindBehaviorMap,
} from '../../utils/kindBehavior.js';
import { SingleFormBodySections } from './SingleFormBodySections.js';
import { SingleFormSidebar } from './SingleFormSidebar.js';
import { FooterActions } from './layout.js';
import {
  buildRecurrenceRule,
  comparePlainDate,
  buildReminderOptions,
  dateOnlyFromPlain,
  durationFromIso,
  isoFromDateAndTime,
  plainFromIso,
  plusMinutes,
  recurrenceBaseFromRule,
  recurrenceUntilFromRule,
  titlePlaceholderForKind,
  timeFromIso,
} from './helpers.js';
import {
  buildKindSegmentedOptions,
  buildKindSelectOptions,
  computeSingleFormSeeds,
  findRelatedEvents,
  pickValidationError,
} from './single-form-helpers.js';
import type { EventComposerSubmitDraft } from './types.js';
import type {
  CalendarLocationType,
  CalendarMeetingProvider,
} from '../../utils/location.js';

export interface SingleFormProps {
  readonly event?: CalendarEvent | null | undefined;
  readonly draft?: Partial<EventComposerSubmitDraft> | undefined;
  readonly kindTheme: CalendarKindMap;
  readonly kindBehavior: CalendarKindBehaviorMap;
  readonly groups?: readonly CalendarGroup[] | undefined;
  readonly relatedEvents?: readonly CalendarEvent[] | undefined;
  readonly linkOptionsForKind?: ((kind: string) => readonly CalendarEventLink[]) | undefined;
  readonly onSelectRelatedEvent?: ((event: CalendarEvent) => void) | undefined;
  readonly onSubmit: (next: EventComposerSubmitDraft) => void;
  readonly onCancel: () => void;
  readonly onDelete?: ((id: string) => void) | undefined;
  readonly defaultDurationMinutes: number;
  readonly recurrenceEnabled: boolean;
  readonly remindersEnabled: boolean;
  readonly locale?: string | undefined;
  readonly mobile: boolean;
  readonly viewerTz?: string | undefined;
}

export function SingleForm(props: SingleFormProps): ReactNode {
  const messages = useCalendarMessages();
  const {
    event,
    draft,
    kindTheme,
    kindBehavior,
    groups,
    relatedEvents = [],
    linkOptionsForKind,
    onSelectRelatedEvent,
    onSubmit,
    onCancel,
    onDelete,
    defaultDurationMinutes,
    recurrenceEnabled,
    remindersEnabled,
    locale,
    mobile,
    viewerTz,
  } = props;
  const editing = event !== null && event !== undefined;

  const seeds = useMemo(
    () => computeSingleFormSeeds({ event, draft, kindTheme, kindBehavior, defaultDurationMinutes }),
    [event, draft, kindTheme, kindBehavior, defaultDurationMinutes],
  );

  const [title, setTitle] = useState(seeds.title);
  const [kind, setKind] = useState<string>(seeds.kind);
  const [allDay, setAllDay] = useState<boolean>(seeds.allDay);
  const [date, setDate] = useState<PlainDate | null>(plainFromIso(seeds.start, viewerTz));
  const [time, setTime] = useState<TimePickerValue>(timeFromIso(seeds.start, viewerTz));
  const [duration, setDuration] = useState<string>(
    durationFromIso(seeds.start, seeds.end, seeds.durationMinutes),
  );
  const [groupId, setGroupId] = useState<string | null>(seeds.groupId);
  const [crossPosted, setCrossPosted] = useState<readonly string[]>(seeds.crossPosted);
  const [meta, setMeta] = useState<string>(seeds.meta);
  const [recurring, setRecurring] = useState<boolean>(seeds.recurring);
  const [recurrence, setRecurrence] = useState<string>(
    recurrenceBaseFromRule(seeds.recurrence),
  );
  const [recurrenceUntil, setRecurrenceUntil] =
    useState<PlainDate | null>(recurrenceUntilFromRule(seeds.recurrence, viewerTz));
  const [reminderMinutesBefore, setReminderMinutesBefore] =
    useState<number | null>(seeds.reminderMinutesBefore);
  const [link, setLink] = useState<CalendarEventLink | null>(seeds.link);
  const [locationType, setLocationType] =
    useState<CalendarLocationType | null>(seeds.locationType);
  const [meetingProvider, setMeetingProvider] =
    useState<CalendarMeetingProvider>(seeds.meetingProvider);
  const [meetingDetail, setMeetingDetail] = useState<string>(seeds.meetingDetail);
  const [locationText, setLocationText] = useState<string>(seeds.locationText);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (duration === '0') setAllDay(true);
    else if (duration !== '0' && allDay) setAllDay(false);
  }, [allDay, duration]);

  const linkOptions = useMemo(
    () => linkOptionsForKind?.(kind) ?? [],
    [linkOptionsForKind, kind],
  );
  const kindOptions = useMemo(() => buildKindSegmentedOptions(kindTheme), [kindTheme]);
  const kindSelectOptions = useMemo(() => buildKindSelectOptions(kindTheme), [kindTheme]);
  const reminderOptions = useMemo(() => buildReminderOptions(messages), [messages]);
  const titlePlaceholder = titlePlaceholderForKind(kind, messages);
  const recurrenceRule = useMemo(
    () => buildRecurrenceRule(recurrence, recurrenceUntil, viewerTz),
    [recurrence, recurrenceUntil, viewerTz],
  );

  const applyKind = useCallback((nextKind: string) => {
    setKind(nextKind);
    setLink((current) => (current !== null && (linkOptionsForKind?.(nextKind) ?? []).some(
      (option) => option.kind === current.kind && option.id === current.id,
    ) ? current : null));
    if (editing) return;
    const defaults = resolveKindDefaults(kindBehavior, nextKind, {
      fallbackDurationMinutes: defaultDurationMinutes,
    });
    setDuration(String(defaults.durationMinutes));
    setAllDay(defaults.durationMinutes === 0);
    setReminderMinutesBefore(defaults.reminderMinutesBefore);
    setRecurring(defaults.recurring);
    setRecurrence(recurrenceBaseFromRule(defaults.recurrence));
    setRecurrenceUntil(recurrenceUntilFromRule(defaults.recurrence, viewerTz));
  }, [defaultDurationMinutes, editing, kindBehavior, linkOptionsForKind, viewerTz]);

  const previewEvent: CalendarEvent = useMemo(() => {
    let startISO: string;
    let endISO: string | null;
    if (date === null) {
      startISO = new Date().toISOString();
      endISO = null;
    } else if (allDay) {
      startISO = dateOnlyFromPlain(date);
      endISO = null;
    } else {
      startISO = isoFromDateAndTime(date, time, viewerTz);
      const minutes = parseInt(duration, 10);
      endISO = minutes > 0 ? plusMinutes(startISO, minutes) : startISO;
    }
    return {
      id: '__preview__',
      title: title.trim().length > 0 ? title.trim() : titlePlaceholder,
      kind,
      startsAt: startISO,
      endsAt: endISO,
      allDay,
      groupId,
      crossPostedTo: crossPosted.filter((g) => g !== groupId),
      ...(meta.length > 0 ? { meta } : {}),
      recurring: recurrenceEnabled && recurring,
      recurrence: recurrenceEnabled && recurring ? recurrenceRule : null,
      ...(remindersEnabled ? { reminderMinutesBefore } : {}),
      status: 'scheduled',
      ...(link !== null ? { links: [link] } : {}),
      locationType,
      meetingProvider: locationType === 'online' ? meetingProvider : null,
      meetingDetail: locationType === 'online' ? meetingDetail.trim() || null : null,
      locationText:
        locationType === 'inPerson' || locationType === 'custom'
          ? locationText.trim() || null
          : null,
    };
  }, [
    title,
    kind,
    allDay,
    date,
    time,
    duration,
    groupId,
    crossPosted,
    meta,
    recurring,
    recurrenceRule,
    recurrenceEnabled,
    reminderMinutesBefore,
    remindersEnabled,
    link,
    locationType,
    meetingProvider,
    meetingDetail,
    locationText,
    viewerTz,
    titlePlaceholder,
  ]);

  const relatedMatches = useMemo(
    () => findRelatedEvents({
      event: previewEvent,
      events: relatedEvents,
      editingEventId: event?.id,
      viewerTz,
    }),
    [event?.id, previewEvent, relatedEvents, viewerTz],
  );
  const hasExactDuplicate = !editing && relatedMatches.some((match) => match.exact);

  const submit = useCallback(() => {
    const validation = pickValidationError(title, date !== null, messages);
    if (validation !== null) {
      setError(validation);
      return;
    }
    const safeDate = date!;
    if (recurrenceEnabled && recurring && recurrenceUntil !== null && comparePlainDate(recurrenceUntil, safeDate) < 0) {
      setError(messages.composerErrorRepeatUntilBeforeStart);
      return;
    }
    if (hasExactDuplicate) {
      setError(messages.composerErrorDuplicateEvent);
      return;
    }
    let startISO: string;
    let endISO: string | null;
    if (allDay) {
      startISO = dateOnlyFromPlain(safeDate);
      endISO = null;
    } else {
      startISO = isoFromDateAndTime(safeDate, time, viewerTz);
      const minutes = parseInt(duration, 10);
      endISO = minutes > 0 ? plusMinutes(startISO, minutes) : null;
    }
    setError(null);
    onSubmit({
      title: title.trim(),
      kind,
      startsAt: startISO,
      endsAt: endISO,
      allDay,
      groupId,
      crossPostedTo: crossPosted.filter((g) => g !== groupId),
      meta: meta.trim(),
      recurring: recurrenceEnabled && recurring,
      recurrence: recurrenceEnabled && recurring ? recurrenceRule : null,
      ...(remindersEnabled ? { reminderMinutesBefore } : {}),
      link,
      locationType,
      meetingProvider: locationType === 'online' ? meetingProvider : null,
      meetingDetail: locationType === 'online' ? meetingDetail.trim() || null : null,
      locationText:
        locationType === 'inPerson' || locationType === 'custom'
          ? locationText.trim() || null
          : null,
      ...(editing && event !== null && event !== undefined ? { id: event.id } : {}),
    });
  }, [
    title,
    date,
    time,
    duration,
    allDay,
    kind,
    groupId,
    crossPosted,
    meta,
    recurring,
    recurrenceUntil,
    recurrenceRule,
    recurrenceEnabled,
    hasExactDuplicate,
    reminderMinutesBefore,
    remindersEnabled,
    link,
    locationType,
    meetingProvider,
    meetingDetail,
    locationText,
    editing,
    event,
    onSubmit,
    viewerTz,
    messages,
  ]);

  const btnSize: ButtonSize = 'md';
  const blockingMessage = error ?? (hasExactDuplicate ? messages.composerErrorDuplicateEvent : null);

  return (
    <>
      <div className="vq-cal__composer-scroll">
        <div className="vq-cal__composer-single">
          <SingleFormBodySections
            mobile={mobile}
            locale={locale}
            kind={kind}
            kindOptions={kindOptions}
            kindSelectOptions={kindSelectOptions}
            onKindChange={applyKind}
            title={title}
            onTitleChange={setTitle}
            titlePlaceholder={titlePlaceholder}
            error={error}
            linkOptions={linkOptions}
            link={link}
            onLinkChange={setLink}
            date={date}
            onDateChange={setDate}
            time={time}
            onTimeChange={setTime}
            allDay={allDay}
            duration={duration}
            onDurationChange={setDuration}
            remindersEnabled={remindersEnabled}
            reminderOptions={reminderOptions}
            reminderMinutesBefore={reminderMinutesBefore}
            onReminderMinutesBeforeChange={setReminderMinutesBefore}
            groups={groups}
            groupId={groupId}
            onGroupIdChange={setGroupId}
            crossPosted={crossPosted}
            onCrossPostedChange={setCrossPosted}
            locationType={locationType}
            onLocationTypeChange={setLocationType}
            meetingProvider={meetingProvider}
            onMeetingProviderChange={setMeetingProvider}
            meetingDetail={meetingDetail}
            onMeetingDetailChange={setMeetingDetail}
            locationText={locationText}
            onLocationTextChange={setLocationText}
            recurrenceEnabled={recurrenceEnabled}
            recurring={recurring}
            onRecurringChange={setRecurring}
            recurrence={recurrence}
            onRecurrenceChange={setRecurrence}
            recurrenceUntil={recurrenceUntil}
            onRecurrenceUntilChange={setRecurrenceUntil}
            meta={meta}
            onMetaChange={setMeta}
            blockingMessage={blockingMessage}
          />

          <SingleFormSidebar
            previewEvent={previewEvent}
            kindTheme={kindTheme}
            groups={groups}
            relatedMatches={relatedMatches}
            onSelectRelatedEvent={onSelectRelatedEvent}
          />
        </div>
      </div>

      <FooterActions
        primary={
          <Button variant="primary" size={btnSize} onClick={submit} disabled={hasExactDuplicate}>
            {editing ? messages.composerSave : messages.composerAdd}
          </Button>
        }
        secondary={
          <Button variant="ghost" size={btnSize} onClick={onCancel}>
            {messages.composerCancel}
          </Button>
        }
        leading={
          editing && onDelete !== undefined && event !== null && event !== undefined ? (
            <Button
              variant="danger"
              size={btnSize}
              onClick={() => {
                onDelete(event.id);
                onCancel();
              }}
            >
              {messages.composerDelete}
            </Button>
          ) : null
        }
      />
    </>
  );
}
